import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import NapOverlay from "../map/NapOverlay";
import NapTableModal from "../modals/NapTableModal";
import NapDetailsResultModal from "../modals/NapDetailsResultModal";
import { geocodeSubscriberAddress } from "../../services/geocodingService";
import MeasureTool from "../map/MeasureTool";
import {
  SERVICE_AREA_CENTER,
  SERVICE_AREA_ZOOM,
  getBarangayCoords,
} from "../../data/barangayGeodata";
import { MODULE_THEMES } from "../../utils/moduleTheme";

const MIG = MODULE_THEMES.migration;

const STATUS_COLORS = {
  Completed: "#22c55e",
  Cancelled: "#ef4444",
  Rescheduled: "#f97316",
  Pending: "#eab308",
};

// Smaller mobile subscriber search pin
const SUBSCRIBER_SEARCH_ICON_MOBILE = L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
    <div style="
      display:flex;align-items:center;justify-content:center;
      width:38px;height:38px;border-radius:50%;
      background:#eab308;border:3px solid #fff;
      box-shadow:0 3px 10px rgba(234,179,8,0.6);
      animation:mig-mobile-pulse 2s ease-out infinite;
    "><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg></div>
    <div style="width:3px;height:8px;background:#dc2626;"></div>
    <div style="width:10px;height:10px;background:#dc2626;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 4px rgba(220,38,38,0.7);"></div>
  </div>`,
  iconSize: [38, 56],
  iconAnchor: [19, 56],
  popupAnchor: [0, -56],
});

const MY_LOCATION_ICON = L.divIcon({
  className: "",
  html: `<div style="position:relative;width:24px;height:24px;">
    <div style="position:absolute;inset:0;border-radius:50%;background:rgba(59,130,246,0.2);animation:mig-mobile-pulse 2s ease-out infinite;"></div>
    <div style="position:absolute;top:4px;left:4px;width:16px;height:16px;border-radius:50%;background:#3b82f6;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Inject pulse animation
if (!document.querySelector("#mig-mobile-pulse-style")) {
  const style = document.createElement("style");
  style.id = "mig-mobile-pulse-style";
  style.textContent = `
    @keyframes mig-mobile-pulse {
      0% { box-shadow: 0 0 0 0 rgba(234,179,8,0.5); }
      70% { box-shadow: 0 0 0 15px rgba(234,179,8,0); }
      100% { box-shadow: 0 0 0 0 rgba(234,179,8,0); }
    }
  `;
  document.head.appendChild(style);
}

// Measure tooltip styles
if (!document.querySelector("#mig-mobile-measure-styles")) {
  const style = document.createElement("style");
  style.id = "mig-mobile-measure-styles";
  style.textContent = `
    .measure-tooltip {
      background: rgba(255,255,255,0.95) !important;
      border: 1px solid #f97316 !important;
      border-radius: 4px !important;
      padding: 2px 6px !important;
      box-shadow: 0 1px 4px rgba(0,0,0,0.15) !important;
      font-family: inherit;
      white-space: nowrap;
    }
    .measure-tooltip::before { border-right-color: #f97316 !important; }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------
function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 15, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

function MapClickToPlace({ active, onPlaced }) {
  const enabledAtRef = useRef(0);
  useEffect(() => {
    if (active) enabledAtRef.current = Date.now();
  }, [active]);
  useMapEvents({
    click(e) {
      if (!active) return;
      if (Date.now() - enabledAtRef.current < 100) return;
      if (e.originalEvent?.target?.closest(".leaflet-popup, .leaflet-control")) return;
      onPlaced(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function FitMarkerBounds({ records }) {
  const map = useMap();
  useEffect(() => {
    const pts = (records || []).filter((r) => r.latitude && r.longitude);
    if (pts.length === 0) return;
    const bounds = L.latLngBounds(pts.map((r) => [r.latitude, r.longitude]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [records, map]);
  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MigrationMobileMap({ dateFrom, onDateChange, onSwitchToPage, pendingLocate, pendingNapFocus }) {
  const { myLocation, fstLocationError, fstLocationPermission, hasPerm } = useAuth();
  const [mapRecords, setMapRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [baseMap, setBaseMap] = useState("satellite");
  const [showLabels, setShowLabels] = useState(true);
  const [layerOpen, setLayerOpen] = useState(false);
  const canViewNapOnMap = hasPerm("nap_locator.view_on_ni_map");
  const [napEnabled, setNapEnabled] = useState(true);
  const [selectedNap, setSelectedNap] = useState(null);
  const [napRecord, setNapRecord] = useState(null);
  const [napRecordLoading, setNapRecordLoading] = useState(false);
  const [qrView, setQrView] = useState(null);
  const [fetchingNapDetails, setFetchingNapDetails] = useState(false);
  const [napDetailsModal, setNapDetailsModal] = useState(null);
  const [napTableModalSearch, setNapTableModalSearch] = useState(null);
  const abortRef = useRef(null);

  // Measure tool state
  const [measureEnabled, setMeasureEnabled] = useState(false);
  const [measureTotal, setMeasureTotal] = useState(0);
  const [measureDrawing, setMeasureDrawing] = useState(true);
  const [measureDoneSignal, setMeasureDoneSignal] = useState(0);
  const [measureUndoSignal, setMeasureUndoSignal] = useState(0);

  // Subscriber locate state
  const [subscriberPin, setSubscriberPin] = useState(null);
  const [subscriberLocating, setSubscriberLocating] = useState(false);
  const [subscriberLocateMsg, setSubscriberLocateMsg] = useState(null);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editLocConfirm, setEditLocConfirm] = useState(false);
  const [editLocSaving, setEditLocSaving] = useState(false);
  const [locateTrigger, setLocateTrigger] = useState(0);
  const pendingLocateRef = useRef(pendingLocate || null);
  const locateExecuted = useRef(false);

  // Accept new pending locate requests
  useEffect(() => {
    if (pendingLocate) {
      pendingLocateRef.current = pendingLocate;
      locateExecuted.current = false;
      setSubscriberPin(null);
      setEditingLocation(false);
      setEditLocConfirm(false);
      setSubscriberLocateMsg(null);
      setLocateTrigger((c) => c + 1);
    }
  }, [pendingLocate]);

  // Focus a NAP location when opened from a record's NAP code (Mig detail modal).
  // Looks up the NAP coordinates, enables the overlay and flies to it.
  const napFocusDoneRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    const code = pendingNapFocus?.napCode;
    if (!code) return;
    if (napFocusDoneRef.current) return;
    napFocusDoneRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/nap-locator", {
          params: { search: code, page: 1, page_size: 20, sort_by: "nap_code", sort_dir: "asc" },
        });
        const norm = (s) => String(s || "").trim().toUpperCase().replace(/\s+/g, " ");
        const match = (Array.isArray(data) ? data : []).find((o) => norm(o.nap_code) === norm(code));
        if (!match || match.latitude == null || match.longitude == null) return;
        if (canViewNapOnMap) setNapEnabled(true);
        setSelectedRecord(null);
        setSelectedNap({ id: match.id, nap_code: match.nap_code, latitude: match.latitude, longitude: match.longitude });
        setFlyTarget([match.latitude, match.longitude]);
        setFlyZoom(18);
      } catch { /* ignore lookup failures */ }
    })();
  }, [loading, pendingNapFocus, canViewNapOnMap]);

  // Fetch NAP details when a NAP point is clicked
  useEffect(() => {
    if (!selectedNap?.id) { setNapRecord(null); return; }
    setNapRecordLoading(true);
    api.get("/nap-locator", { params: { search: selectedNap.nap_code, page: 1, page_size: 1 } })
      .then((res) => { setNapRecord(res.data.find((r) => r.id === selectedNap.id) || res.data[0] || null); })
      .catch(() => setNapRecord(null))
      .finally(() => setNapRecordLoading(false));
  }, [selectedNap]);

  // Fetch map records
  const fetchData = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = {};
      if (dateFrom) { params.date_from = dateFrom; params.date_to = dateFrom; }
      const { data } = await api.get("/migration/dispatch-dashboard/map-records", { params, signal: controller.signal });
      setMapRecords(data);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      setMapRecords([]);
    } finally { setLoading(false); }
  }, [dateFrom]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Stats
  const stats = useMemo(() => {
    const c = { Pending: 0, Completed: 0, Rescheduled: 0, Cancelled: 0 };
    for (const r of mapRecords) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [mapRecords]);

  // Process pending subscriber locate
  useEffect(() => {
    if (loading) return;
    const req = pendingLocateRef.current;
    if (!req) return;
    if (locateExecuted.current) return;
    locateExecuted.current = true;
    pendingLocateRef.current = null;

    const hasCoords = req.latitude && req.longitude && !isNaN(req.latitude) && !isNaN(req.longitude);
    let immediateCoords = null;

    if (hasCoords) {
      immediateCoords = [parseFloat(req.latitude), parseFloat(req.longitude)];
    } else {
      immediateCoords = getBarangayCoords(req.municipality, req.barangay);
    }

    if (immediateCoords) {
      const pin = {
        id: req.id || null,
        lat: immediateCoords[0],
        lng: immediateCoords[1],
        name: req.customerName,
        address: req.address,
        municipality: req.municipality,
        barangay: req.barangay,
      };
      setSubscriberPin(pin);
      setSelectedRecord(null);
      setSelectedNap(null);
      setFlyTarget(immediateCoords);
      setFlyZoom(hasCoords ? 18 : 16);
      setEditingLocation(true);
      setEditLocConfirm(false);
      if (hasCoords) {
        setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
        setSubscriberLocating(false);
        return; // saved or existing coords — no need for geocoding
      }
      setSubscriberLocating(true);
      setSubscriberLocateMsg({ type: "warning", text: "Showing barangay area. Searching for exact address…" });
    } else {
      setSubscriberLocating(true);
      setSubscriberLocateMsg(null);
    }

    // Phase 2: Geocoding
    const refineLocation = async () => {
      try {
        const result = await geocodeSubscriberAddress({
          address: req.address,
          barangay: req.barangay,
          municipality: req.municipality,
          latitude: null,
          longitude: null,
        });

        if (result.success && result.latitude && result.longitude) {
          const pin = {
            id: req.id || null,
            lat: result.latitude,
            lng: result.longitude,
            name: req.customerName,
            address: req.address,
            municipality: req.municipality,
            barangay: req.barangay,
          };
          setSubscriberPin(pin);
          setFlyTarget([result.latitude, result.longitude]);
          setFlyZoom(18);
          setEditingLocation(true);
          setEditLocConfirm(false);
          const msgType = (result.accuracy === "exact" || result.accuracy === "address") ? "success" : "warning";
          setSubscriberLocateMsg({ type: msgType, text: result.message });
        } else if (!immediateCoords) {
          setSubscriberLocateMsg({ type: "error", text: result.message });
        } else {
          setEditingLocation(true);
          setEditLocConfirm(false);
          setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
        }
      } catch {
        if (!immediateCoords) {
          setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
        } else {
          setEditingLocation(true);
          setEditLocConfirm(false);
          setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
        }
      } finally {
        setSubscriberLocating(false);
      }
    };

    refineLocation();
  }, [loading, locateTrigger]);

  // Auto-dismiss locate message after 5s
  useEffect(() => {
    if (!subscriberLocateMsg) return;
    const t = setTimeout(() => setSubscriberLocateMsg(null), 5000);
    return () => clearTimeout(t);
  }, [subscriberLocateMsg]);

  // Handler: NAP details
  const handleNapDetails = async () => {
    if (!napRecord) return;
    setFetchingNapDetails(true);
    try {
      const { data } = await api.post(`/nap-locator/${napRecord.id}/nap-details`);
      if (data.found) {
        setNapDetailsModal(data);
      } else {
        alert(data.detail || `No NAP details found for "${napRecord.nap_code}" on the external server.`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to fetch NAP details from external server.");
    } finally {
      setFetchingNapDetails(false);
    }
  };

  // Handler: view on table
  const handleViewOnTable = () => {
    if (!napRecord) return;
    const parts = napRecord.nap_code.trim().split(/\s+/);
    const lcp = parts.length >= 2 ? `${parts[0]} ${parts[1]}` : napRecord.nap_code;
    setNapTableModalSearch(lcp);
  };

  // Handle name click from bottom sheet — trigger locate
  const handleNameLocate = (r) => {
    setSelectedRecord(null);
    pendingLocateRef.current = {
      id: r.id,
      customerName: r.name,
      address: r.address || r.complete_address,
      municipality: r.municipality,
      barangay: r.barangay,
      latitude: r.latitude || null,
      longitude: r.longitude || null,
      locationSaved: r.location_saved || false,
    };
    locateExecuted.current = false;
    setSubscriberPin(null);
    setEditingLocation(false);
    setEditLocConfirm(false);
    setSubscriberLocateMsg(null);
    setLocateTrigger((c) => c + 1);
  };

  const navigateDate = (days) => {
    const d = new Date(dateFrom + "T00:00:00");
    d.setDate(d.getDate() + days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const newDate = `${y}-${m}-${dd}`;
    onDateChange(newDate);
    setSelectedRecord(null);
    setFlyTarget(null);
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Date nav + toggle */}
      <div className="flex-shrink-0 px-3 pt-2 pb-1 flex items-center gap-1.5">
        <button type="button" onClick={() => navigateDate(-1)} className={`flex-shrink-0 text-xs text-white ${MIG.btnPrimaryStatic} w-8 h-8 rounded font-bold flex items-center justify-center`}>&lt;</button>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { onDateChange(e.target.value); setSelectedRecord(null); }}
          className="flex-1 border rounded-lg px-2 py-1 text-xs bg-white min-w-0"
        />
        <button type="button" onClick={() => navigateDate(1)} className={`flex-shrink-0 text-xs text-white ${MIG.btnPrimaryStatic} w-8 h-8 rounded font-bold flex items-center justify-center`}>&gt;</button>

        <div className="flex flex-shrink-0 ml-1">
          <button
            type="button"
            className={`px-3 py-1.5 text-[10px] font-semibold rounded-l-lg border ${MIG.btnPrimaryStatic}`}
          >
            Map
          </button>
          <button
            type="button"
            onClick={onSwitchToPage}
            className="px-3 py-1.5 text-[10px] font-semibold rounded-r-lg border bg-white text-gray-600"
          >
            Page
          </button>
        </div>
      </div>

      {/* Status cards */}
      <div className="flex-shrink-0 grid grid-cols-4 gap-1.5 px-3 pb-1.5">
        {[
          { label: "Pending", key: "Pending", color: "bg-yellow-50 text-yellow-800 border-yellow-200" },
          { label: "Done", key: "Completed", color: "bg-green-50 text-green-800 border-green-200" },
          { label: "Resched", key: "Rescheduled", color: "bg-orange-50 text-orange-800 border-orange-200" },
          { label: "Cancel", key: "Cancelled", color: "bg-red-50 text-red-800 border-red-200" },
        ].map(({ label, key, color }) => (
          <div key={key} className={`rounded-lg px-1 py-1.5 text-center border text-xs font-medium ${color}`}>
            <p className="text-[9px] uppercase tracking-wide">{label}</p>
            <p className="text-sm font-bold">{stats[key] || 0}</p>
          </div>
        ))}
      </div>

      {/* Map */}
      <div className="flex-1 relative isolate">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading…</div>
        ) : (
          <MapContainer
            center={SERVICE_AREA_CENTER}
            zoom={SERVICE_AREA_ZOOM}
            maxZoom={21}
            style={{ height: "100%", width: "100%", background: "#e5e7eb" }}
            scrollWheelZoom
          >
            {baseMap === "satellite" ? (
              <TileLayer key="sat" attribution="&copy; Google" url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" maxZoom={21} />
            ) : (
              <TileLayer key="road" attribution='&copy; CARTO &copy; OSM' url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png" />
            )}
            {showLabels && (
              <TileLayer key="labels" attribution="&copy; Google" url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}" maxZoom={21} zIndex={500} />
            )}
            {flyTarget && <FlyTo center={flyTarget} zoom={flyZoom} />}
            {!flyTarget && <FitMarkerBounds records={mapRecords} />}

            {/* JO house markers */}
            {mapRecords.filter((r) => r.latitude && r.longitude).map((r) => {
              const color = STATUS_COLORS[r.status] || "#9ca3af";
              return (
                <Marker
                  key={`mig-mob-${r.id}`}
                  position={[r.latitude, r.longitude]}
                  pane="markerPane"
                  icon={L.divIcon({
                    className: "",
                    html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));">
                        <path d="M12 3L2 12h3v8h6v-5h2v5h6v-8h3L12 3z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
                      </svg>
                    </div>`,
                    iconSize: [18, 18],
                    iconAnchor: [9, 18],
                  })}
                  eventHandlers={{
                    click: () => {
                      setSelectedRecord(r);
                      setSelectedNap(null);
                      setFlyTarget([r.latitude, r.longitude]);
                      setFlyZoom(17);
                    },
                  }}
                >
                  <Tooltip direction="top" offset={[0, -18]}>
                    <div className="text-xs">
                      <div className="font-bold">{r.oldacctno || r.name}</div>
                      <div>{r.name}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                        {r.status}
                      </div>
                    </div>
                  </Tooltip>
                </Marker>
              );
            })}

            {/* My Location marker */}
            {myLocation && (
              <Marker position={[myLocation.latitude, myLocation.longitude]} icon={MY_LOCATION_ICON} pane="markerPane">
                <Tooltip direction="top" offset={[0, -20]} permanent>
                  <span className="text-[10px] font-bold text-blue-700">You</span>
                </Tooltip>
              </Marker>
            )}

            {/* NAP overlay */}
            {canViewNapOnMap && (
              <NapOverlay
                enabled={napEnabled}
                onPointClick={(pt) => { setSelectedNap(pt); setSelectedRecord(null); }}
                selectedNapId={selectedNap?.id || null}
              />
            )}

            {/* Subscriber locate pin */}
            {subscriberPin && (
              <Marker position={[subscriberPin.lat, subscriberPin.lng]} icon={SUBSCRIBER_SEARCH_ICON_MOBILE} zIndexOffset={2000}>
                <Popup>
                  <div className="text-xs space-y-0.5">
                    <div className="font-bold text-gray-800">{subscriberPin.name}</div>
                    {subscriberPin.address && <div className="text-gray-600">{subscriberPin.address}</div>}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Click-to-repin */}
            <MapClickToPlace
              active={editingLocation && !editLocConfirm}
              onPlaced={(lat, lng) => {
                setSubscriberPin((prev) => prev ? { ...prev, lat, lng } : null);
              }}
            />

            {/* Measure tool */}
            <MeasureTool
              enabled={measureEnabled}
              onTotalChange={setMeasureTotal}
              onDrawingChange={setMeasureDrawing}
              doneSignal={measureDoneSignal}
              undoSignal={measureUndoSignal}
            />
          </MapContainer>
        )}

        {/* Subscriber locating spinner */}
        {subscriberLocating && (
          <div className="absolute top-2 left-2 z-[1100] bg-white/90 rounded-lg px-3 py-2 shadow flex items-center gap-2">
            <svg className="animate-spin h-4 w-4 text-blue-500" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            <span className="text-xs text-gray-600">Locating…</span>
          </div>
        )}

        {/* Subscriber locate message */}
        {subscriberLocateMsg && !subscriberLocating && (
          <div className={`absolute top-2 left-2 z-[1100] rounded-lg px-3 py-2 shadow text-xs font-medium ${
            subscriberLocateMsg.type === "success" ? "bg-green-100 text-green-800" :
            subscriberLocateMsg.type === "warning" ? "bg-yellow-100 text-yellow-800" :
            "bg-red-100 text-red-800"
          }`}>
            {subscriberLocateMsg.text}
          </div>
        )}

        {/* Editing location toolbar — pinned to the visual viewport bottom with
            `fixed` (not `absolute`) so it stays visible on every phone. Using
            `absolute bottom-0` inside the h-screen map container pushed it below
            the fold on browsers where 100vh exceeds the visible viewport. The
            safe-area padding keeps the buttons clear of the iOS home indicator /
            Android gesture bar. */}
        {editingLocation && subscriberPin && (
          <div
            className="fixed bottom-0 left-0 right-0 z-[2000] bg-gray-900/95 text-white px-4 pt-3 flex items-center gap-2 flex-wrap shadow-[0_-4px_12px_rgba(0,0,0,0.35)]"
            style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
          >
            {!editLocConfirm ? (
              <>
                <span className="text-xs flex-1">📍 Tap on map to adjust pin</span>
                <button
                  onClick={() => setEditLocConfirm(true)}
                  disabled={!subscriberPin}
                  className="px-3 py-1.5 bg-emerald-500 active:bg-emerald-400 disabled:opacity-40 rounded text-xs font-semibold"
                >Save</button>
                <button
                  onClick={() => { setEditingLocation(false); setEditLocConfirm(false); setSubscriberPin(null); setSubscriberLocateMsg(null); }}
                  className="px-3 py-1.5 bg-white/20 active:bg-white/30 rounded text-xs"
                >Cancel</button>
              </>
            ) : (
              <>
                <span className="text-xs flex-1">Save this as subscriber's location?</span>
                <button
                  onClick={async () => {
                    const recId = subscriberPin?.id;
                    if (!recId || !subscriberPin) return;
                    setEditLocSaving(true);
                    try {
                      await api.put(`/migration/dispatch-dashboard/${recId}/coordinates`, {
                        latitude: subscriberPin.lat,
                        longitude: subscriberPin.lng,
                      });
                      setMapRecords((prev) =>
                        prev.map((rec) => rec.id === recId ? { ...rec, latitude: subscriberPin.lat, longitude: subscriberPin.lng, location_saved: true } : rec)
                      );
                      setSubscriberLocateMsg({ type: "success", text: "Location saved." });
                      setSubscriberPin(null);
                      setEditingLocation(false);
                      setEditLocConfirm(false);
                    } catch {
                      setSubscriberLocateMsg({ type: "error", text: "Failed to save coordinates." });
                      setEditLocConfirm(false);
                    } finally {
                      setEditLocSaving(false);
                    }
                  }}
                  disabled={editLocSaving}
                  className="px-3 py-1.5 bg-emerald-500 active:bg-emerald-400 disabled:opacity-50 rounded text-xs font-semibold"
                >{editLocSaving ? "Saving…" : "Confirm"}</button>
                <button
                  onClick={() => setEditLocConfirm(false)}
                  disabled={editLocSaving}
                  className="px-3 py-1.5 bg-white/20 active:bg-white/30 disabled:opacity-50 rounded text-xs"
                >Back</button>
              </>
            )}
          </div>
        )}

        {/* Measure tool controls */}
        {!loading && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-1">
            {!measureEnabled ? (
              <button
                type="button"
                onClick={() => setMeasureEnabled(true)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg shadow-md border border-gray-200 bg-white text-xs font-medium text-gray-700 active:bg-gray-100"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21l6-6m0 0l4-4m-4 4l-4-4m4 4l4 4M21 3l-6 6m0 0l-4 4m4-4l4 4m-4-4l-4-4" />
                </svg>
                Measure
              </button>
            ) : (
              <>
                <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg shadow-md border border-orange-300 bg-orange-500 text-xs font-bold text-white">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21l6-6m0 0l4-4m-4 4l-4-4m4 4l4 4M21 3l-6 6m0 0l-4 4m4-4l4 4m-4-4l-4-4" />
                  </svg>
                  {measureTotal < 1000 ? measureTotal.toFixed(1) + " m" : (measureTotal / 1000).toFixed(2) + " km"}
                </div>
                {measureDrawing && (
                  <>
                    <button
                      type="button"
                      onClick={() => setMeasureUndoSignal((v) => v + 1)}
                      className="px-2 py-1.5 rounded-lg shadow-md border border-gray-200 bg-white text-xs font-medium text-gray-600 active:bg-gray-100"
                      title="Undo last point"
                    >
                      Undo
                    </button>
                    <button
                      type="button"
                      onClick={() => setMeasureDoneSignal((v) => v + 1)}
                      className="px-2 py-1.5 rounded-lg shadow-md border border-green-300 bg-green-500 text-xs font-bold text-white active:bg-green-600"
                    >
                      Done
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => { setMeasureEnabled(false); setMeasureTotal(0); }}
                  className="px-1.5 py-1.5 rounded-lg shadow-md border border-gray-200 bg-white text-gray-500 active:bg-red-50 active:text-red-500"
                  title="Clear & close"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            )}
          </div>
        )}

        {/* Street Names checkbox — below zoom controls */}
        {!loading && (
          <div className="absolute top-[90px] left-[10px] z-[1000]">
            <label className="flex items-center gap-1.5 bg-white rounded-lg shadow-md border border-gray-200 px-2 py-1.5 cursor-pointer active:bg-gray-50">
              <input
                type="checkbox"
                checked={showLabels}
                onChange={(e) => setShowLabels(e.target.checked)}
                className="w-3.5 h-3.5 rounded"
              />
              <span className="text-[11px] font-medium text-gray-700">Street Names</span>
            </label>
          </div>
        )}

        {/* Map layer control */}
        {!loading && (
          <div className="absolute top-2 right-2 z-[1000]">
            <button
              type="button"
              onClick={() => setLayerOpen((v) => !v)}
              className="w-9 h-9 bg-white rounded-lg shadow-md border border-gray-200 flex items-center justify-center active:bg-gray-100"
              aria-label="Map layers"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 17 12 22 22 17" />
                <polyline points="2 12 12 17 22 12" />
              </svg>
            </button>
            {layerOpen && (
              <div className="mt-1.5 bg-white rounded-xl shadow-lg border border-gray-200 p-3 w-[170px] animate-in fade-in">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Base Map</p>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setBaseMap("road")}
                    className={`flex-1 px-2 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
                      baseMap === "road" ? "bg-blue-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 active:bg-gray-200"
                    }`}
                  >Road</button>
                  <button
                    type="button"
                    onClick={() => setBaseMap("satellite")}
                    className={`flex-1 px-2 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
                      baseMap === "satellite" ? "bg-blue-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 active:bg-gray-200"
                    }`}
                  >Satellite</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Selected record detail overlay */}
        {selectedRecord && !editingLocation && (
          <div className="absolute bottom-0 left-0 right-0 z-[1000] bg-white border-t shadow-lg rounded-t-xl max-h-[45%] overflow-y-auto">
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
              <h3 className="text-sm font-bold text-gray-800 truncate">{selectedRecord.oldacctno || selectedRecord.name}</h3>
              <button onClick={() => setSelectedRecord(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
            </div>
            <div className="px-4 pb-4 space-y-1.5 text-xs text-gray-700">
              <div>
                <span className="text-gray-400">Name:</span>{" "}
                <span
                  className="font-semibold text-blue-600 cursor-pointer"
                  onClick={() => handleNameLocate(selectedRecord)}
                >
                  {selectedRecord.name || "—"}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-400">Status:</span>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[selectedRecord.status] || "#9ca3af" }} />
                <span className="font-medium">{selectedRecord.status}</span>
              </div>
              {(selectedRecord.complete_address || selectedRecord.address) && <div><span className="text-gray-400">Address:</span> {selectedRecord.complete_address || selectedRecord.address}</div>}
              <div><span className="text-gray-400">Barangay:</span> {selectedRecord.barangay || "—"}</div>
              <div><span className="text-gray-400">Municipality:</span> {(selectedRecord.municipality || "").replace(/^CITY OF\s+/i, "")}</div>
              {selectedRecord.team_number && <div><span className="text-gray-400">Team:</span> {selectedRecord.team_number}</div>}
              {selectedRecord.bundle_type && <div><span className="text-gray-400">Bundle:</span> {selectedRecord.bundle_type}</div>}
            </div>
          </div>
        )}

        {/* Selected NAP detail overlay */}
        {selectedNap && !selectedRecord && (
          <div className="absolute bottom-0 left-0 right-0 z-[1500] pointer-events-none">
            <div className="pointer-events-auto w-full max-h-[55vh] bg-white rounded-t-2xl shadow-xl overflow-y-auto border-t">
              <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">NAP Details</span>
                <button onClick={() => setSelectedNap(null)} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 text-lg leading-none">&times;</button>
              </div>
              <div className="px-4 py-3">
                {napRecordLoading ? (
                  <p className="text-sm text-gray-400 py-6 text-center">Loading...</p>
                ) : napRecord ? (
                  <div className="space-y-2.5 text-sm text-gray-700">
                    <div><span className="text-gray-400 text-xs">NAP Code:</span> <span className="font-semibold text-gray-900">{napRecord.nap_code}</span></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-gray-400 text-xs">Ports:</span> {napRecord.no_of_ports ?? "—"}</div>
                      <div><span className="text-gray-400 text-xs">Used Port:</span> {napRecord.used_port ?? "—"}</div>
                      <div><span className="text-gray-400 text-xs">Vacant:</span> {napRecord.vacant ?? "—"}</div>
                      <div><span className="text-gray-400 text-xs">Record:</span> {napRecord.stencil ?? "—"}</div>
                      <div><span className="text-gray-400 text-xs">ONU:</span> {napRecord.onu_connect ?? "—"}</div>
                    </div>
                    <div><span className="text-gray-400 text-xs">Area:</span> {napRecord.area || "—"}</div>
                    <div><span className="text-gray-400 text-xs">Lat/Lng:</span> {napRecord.latitude}, {napRecord.longitude}</div>
                    <div><span className="text-gray-400 text-xs">Updated By:</span> {napRecord.updated_by_name || "—"}</div>

                    {/* Action buttons */}
                    <div className="border-t pt-3 mt-3 flex flex-wrap gap-2">
                      {napRecord.qr_code_data && (
                        <button
                          onClick={() => setQrView({ qrData: napRecord.qr_code_data, napCode: napRecord.nap_code })}
                          className="px-3 py-1.5 text-xs bg-gray-800 text-white rounded-lg font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm14 3h.01M17 17h.01M21 17h.01M14 14h3v3h-3v-3zm3 3h3v3h-3v-3z" />
                          </svg>
                          QR Code
                        </button>
                      )}
                      {hasPerm("nap_locator.view_on_ni_map") && (
                        <button
                          onClick={handleNapDetails}
                          disabled={fetchingNapDetails}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium flex items-center gap-1 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {fetchingNapDetails ? "Fetching..." : "NAP Details"}
                        </button>
                      )}
                      {hasPerm("nap_locator.view_on_ni_map") && (
                        <button
                          onClick={handleViewOnTable}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
                          </svg>
                          View on Table
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 py-6 text-center">No details available</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen QR Code overlay */}
      {qrView && (
        <div className="fixed inset-0 z-[3000] bg-white flex flex-col items-center justify-center" onClick={() => setQrView(null)}>
          <div className="absolute top-4 right-4">
            <button onClick={() => setQrView(null)} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 text-xl active:bg-gray-200">&times;</button>
          </div>
          <p className="mb-3 text-sm font-semibold text-gray-800">{qrView.napCode}</p>
          <img
            src={`data:image/png;base64,${qrView.qrData}`}
            alt="QR Code"
            className="w-[80vw] h-[80vw] max-w-[400px] max-h-[400px] object-contain"
          />
          <p className="mt-4 text-sm text-gray-500">Tap anywhere to close</p>
        </div>
      )}

      {/* NAP Details Result Modal */}
      {napDetailsModal && (
        <NapDetailsResultModal
          data={napDetailsModal}
          onClose={() => setNapDetailsModal(null)}
          onChanged={() => { setNapEnabled(false); setTimeout(() => setNapEnabled(true), 50); }}
        />
      )}

      {/* NAP Table Modal */}
      {napTableModalSearch && (
        <NapTableModal key={napTableModalSearch} lcpCode={napTableModalSearch} onClose={() => setNapTableModalSearch(null)} />
      )}

      {/* Loading overlay when fetching NAP details */}
      {fetchingNapDetails && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20">
          <div className="relative flex items-center justify-center w-20 h-20">
            <svg className="absolute inset-0 w-full h-full animate-spin" style={{ animationDuration: "1.5s" }} viewBox="0 0 100 100" fill="none">
              <polygon points="50,2 93,25 93,75 50,98 7,75 7,25" stroke="#2563eb" strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray="12 6" />
            </svg>
            <img src="/best-icon.png" alt="Loading..." className="h-12 w-12" />
          </div>
        </div>
      )}
    </div>
  );
}
